import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useNavigate } from "react-router-dom";
import { Suspense, lazy, useEffect } from "react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { LanguageProvider } from "./context/LanguageContext";
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import NetworkStatus from "./components/NetworkStatus";
import "./App.css";

// Eager-load layouts (always needed)
import DashboardLayout from "./layouts/DashboardLayout";
import AdminLayout from "./layouts/AdminLayout";

// Lazy-load all pages for code splitting
const LandingPage = lazy(() => import("./pages/LandingPage"));
const LoginPage = lazy(() => import("./pages/LoginPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const ServicePage = lazy(() => import("./pages/ServicePage"));
const IndustryPage = lazy(() => import("./pages/IndustryPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const AboutUsPage = lazy(() => import("./pages/AboutUsPage"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Tickets = lazy(() => import("./pages/Tickets"));
const TicketDetail = lazy(() => import("./pages/TicketDetail"));
const CreateTicket = lazy(() => import("./pages/CreateTicket"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Proposals = lazy(() => import("./pages/Proposals"));
const Account = lazy(() => import("./pages/Account"));
const Messages = lazy(() => import("./pages/Messages"));
const Changes = lazy(() => import("./pages/Changes"));
const Team = lazy(() => import("./pages/Client/Team"));
const AdminLoginPage = lazy(() => import("./pages/admin/AdminLoginPage"));
const AdminForgotPasswordPage = lazy(() => import("./pages/admin/AdminForgotPasswordPage"));
const AdminResetPasswordPage = lazy(() => import("./pages/admin/AdminResetPasswordPage"));
const AdminDashboard = lazy(() => import("./pages/admin/AdminDashboard"));
const AdminTeam = lazy(() => import("./pages/admin/AdminTeam"));
const AdminTestimonials = lazy(() => import("./pages/admin/AdminTestimonials"));
const AdminTechnologies = lazy(() => import("./pages/admin/AdminTechnologies"));
const AdminCompanies = lazy(() => import("./pages/admin/AdminCompanies"));
const AdminCustomers = lazy(() => import("./pages/admin/AdminCustomers"));
const AdminAdmins = lazy(() => import("./pages/admin/AdminAdmins"));
const AdminInvoices = lazy(() => import("./pages/admin/AdminInvoices"));
const AdminProposals = lazy(() => import("./pages/admin/AdminProposals"));
const AdminMessages = lazy(() => import("./pages/admin/AdminMessages"));
const AdminTickets = lazy(() => import("./pages/admin/AdminTickets"));
const AdminLoginAudit = lazy(() => import("./pages/admin/AdminLoginAudit"));
const AdminSocialLinks = lazy(() => import("./pages/admin/AdminSocialLinks"));
const AdminClientMessages = lazy(() => import("./pages/admin/AdminClientMessages"));
const AdminSEO = lazy(() => import("./pages/admin/AdminSEO"));
const AdminEmailTemplates = lazy(() => import("./pages/admin/AdminEmailTemplates"));
const AdminActivityLog = lazy(() => import("./pages/admin/AdminActivityLog"));
const AdminCannedResponses = lazy(() => import("./pages/admin/AdminCannedResponses"));
const AdminInterventions = lazy(() => import("./pages/admin/AdminInterventions"));
const AdminChanges = lazy(() => import("./pages/admin/AdminChanges"));
const AdminTimeTracking = lazy(() => import("./pages/admin/AdminTimeTracking"));
const AdminOnboarding = lazy(() => import("./pages/admin/AdminOnboarding"));
const AdminApiSettings = lazy(() => import("./pages/admin/AdminApiSettings"));
const AdminRemoteSessions = lazy(() => import("./pages/admin/AdminRemoteSessions"));
const AdminIntegrationSync = lazy(() => import("./pages/admin/AdminIntegrationSync"));
const AdminClientFiles = lazy(() => import("./pages/admin/AdminClientFiles"));
const AdminFAQ = lazy(() => import("./pages/admin/AdminFAQ"));
const AdminNotificationPrefs = lazy(() => import("./pages/admin/AdminNotificationPrefs"));
const AdminNetwork = lazy(() => import("./pages/admin/AdminNetwork"));
const AdminDocumentation = lazy(() => import("./pages/admin/AdminDocumentation"));
const AdminKnowledgeBase = lazy(() => import("./pages/admin/AdminKnowledgeBase"));
const AdminIPAM = lazy(() => import("./pages/admin/AdminIPAM"));
const AdminAssets = lazy(() => import("./pages/admin/AdminAssets"));
const AdminSyncLog = lazy(() => import("./pages/admin/AdminSyncLog"));
const ApiDocs = lazy(() => import("./pages/Admin/ApiDocs"));
const PerformanceDashboard = lazy(() => import("./pages/Admin/PerformanceDashboard"));
const Interventions = lazy(() => import("./pages/Interventions"));
const ClientOnboarding = lazy(() => import("./pages/ClientOnboarding"));
const ClientFiles = lazy(() => import("./pages/ClientFiles"));
const ClientNetwork = lazy(() => import("./pages/ClientNetwork"));
const ClientKnowledgeBase = lazy(() => import("./pages/ClientKnowledgeBase"));
const ClientIPAM = lazy(() => import("./pages/ClientIPAM"));
const ClientAssets = lazy(() => import("./pages/Client/ClientAssets"));
const ClientNotificationSettings = lazy(() => import("./pages/Client/ClientNotificationSettings"));
const ClientEmails = lazy(() => import("./pages/ClientEmails"));
const FAQ = lazy(() => import("./pages/FAQ"));
const ShareTicketPage = lazy(() => import("./pages/ShareTicketPage"));
const AdminPushNotifications = lazy(() => import("./pages/admin/AdminPushNotifications"));
const AdminNotificationSettings = lazy(() => import("./pages/admin/AdminNotificationSettings"));
const AdminEmailProviders = lazy(() => import("./pages/admin/AdminEmailProviders"));
const AdminNotificationLog = lazy(() => import("./pages/admin/AdminNotificationLog"));
const AdminContracts = lazy(() => import("./pages/admin/AdminContracts"));
const AdminContractDashboard = lazy(() => import("./pages/admin/AdminContractDashboard"));
const AdminContractWizard = lazy(() => import("./pages/admin/AdminContractWizard"));
const AdminClauses = lazy(() => import("./pages/admin/AdminClauses"));
const AdminCompanyIdentity = lazy(() => import("./pages/admin/AdminCompanyIdentity"));
const ClientContracts = lazy(() => import("./pages/ClientContracts"));
const AdminQuotes = lazy(() => import("./pages/admin/AdminQuotes"));
const AdminVendorQuotes = lazy(() => import("./pages/admin/AdminVendorQuotes"));
const AdminPowerGraph = lazy(() => import("./pages/admin/AdminPowerGraph"));
const AdminQuoteBuilder = lazy(() => import("./pages/admin/AdminQuoteBuilder"));
const AdminQuoteWorkspaces = lazy(() => import("./pages/admin/AdminQuoteWorkspaces"));
const AdminQuoteWorkspace = lazy(() => import("./pages/admin/AdminQuoteWorkspace"));
const AdminPackages = lazy(() => import("./pages/admin/AdminPackages"));
const AdminQuoteTemplates = lazy(() => import("./pages/admin/AdminQuoteTemplates"));
const AdminQuoteSettings = lazy(() => import("./pages/admin/AdminQuoteSettings"));
const AdminDolibarrSync = lazy(() => import("./pages/admin/AdminDolibarrSync"));
const AdminGLPISync = lazy(() => import("./pages/admin/AdminGLPISync"));
const AdminExpenses = lazy(() => import("./pages/admin/AdminExpenses"));
const AdminRoles = lazy(() => import("./pages/admin/AdminRoles"));
const AdminClientRoles = lazy(() => import("./pages/admin/AdminClientRoles"));
const AdminCareers = lazy(() => import("./pages/admin/AdminCareers"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const AdminSecurityDashboard = lazy(() => import("./pages/admin/AdminSecurityDashboard"));
const AdminExpenseCategories = lazy(() => import("./pages/admin/AdminExpenseCategories"));
const AdminRackManagement = lazy(() => import("./pages/admin/AdminRackManagement"));
const AdminRackDesigner = lazy(() => import("./pages/admin/AdminRackDesigner"));
const AdminRackReport = lazy(() => import("./pages/admin/AdminRackReport"));
const AdminMaintenanceReport = lazy(() => import("./pages/admin/AdminMaintenanceReport"));
const AdminLocationReport = lazy(() => import("./pages/admin/AdminLocationReport"));
const AdminLocationTopology = lazy(() => import("./pages/admin/AdminLocationTopology"));
const AdminRackUtilization = lazy(() => import("./pages/admin/AdminRackUtilization"));
const ClientRackView = lazy(() => import("./pages/ClientRackView"));
const ClientRackListLazy = lazy(() => import("./pages/ClientRackView").then(m => ({ default: m.ClientRackList })));
const AdminSLATracking = lazy(() => import("./pages/admin/AdminSLATracking"));
const AdminMaintenanceNotices = lazy(() => import("./pages/admin/AdminMaintenanceNotices"));
const AdminAssessments = lazy(() => import("./pages/admin/AdminAssessments"));
const AdminAssessmentTemplates = lazy(() => import("./pages/admin/AdminAssessmentTemplates"));
const AdminAssessmentBuilder = lazy(() => import("./pages/admin/AdminAssessmentBuilder"));
const AdminPostmortems = lazy(() => import("./pages/admin/AdminPostmortems"));
const AdminPostmortemBuilder = lazy(() => import("./pages/admin/AdminPostmortemBuilder"));
const ClientMaintenanceNotices = lazy(() => import("./pages/client/ClientMaintenanceNotices"));
const AdminScheduledTickets = lazy(() => import("./pages/admin/AdminScheduledTickets"));
const AdminServiceCatalog = lazy(() => import("./pages/admin/AdminServiceCatalog"));
const AdminClientHours = lazy(() => import("./pages/admin/AdminClientHours"));
const ClientQuotes = lazy(() => import("./pages/ClientQuotes"));
const ClientExpenses = lazy(() => import("./pages/ClientExpenses"));
const ClientServiceCatalog = lazy(() => import("./pages/Client/ClientServiceCatalog"));
const ClientHours = lazy(() => import("./pages/Client/ClientHours"));
const ClientAssessments = lazy(() => import("./pages/Client/ClientAssessments"));

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#1e3a5f]"></div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

const PageLoader = () => (
  <div className="min-h-screen bg-slate-100 flex items-center justify-center">
    <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-[#1e3a5f]"></div>
  </div>
);

// PWA Smart Start - redirects to appropriate dashboard when opened as PWA
const PWASmartStart = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    const isPWAParam = searchParams.get('pwa') === 'true';
    const isPWAMode = window.matchMedia('(display-mode: standalone)').matches || 
                      window.navigator.standalone === true;
    
    if (isPWAParam || isPWAMode) {
      // Check for admin token first
      const adminToken = localStorage.getItem('adminToken');
      if (adminToken) {
        navigate('/admin/dashboard', { replace: true });
        return;
      }
      
      // Check for client token
      const clientToken = localStorage.getItem('token');
      if (clientToken) {
        navigate('/dashboard', { replace: true });
        return;
      }
      
      // No one logged in - check last used portal
      const lastPortal = localStorage.getItem('lastPortal');
      if (lastPortal === 'admin') {
        navigate('/admin/login', { replace: true });
        return;
      }
    }
    
    // Default: show landing page (component will render it)
  }, [navigate, searchParams]);
  
  return <LandingPage />;
};

function AppRoutes() {
  return (
    <Suspense fallback={<PageLoader />}>
    <Routes>
      {/* Public Routes - PWA Smart Start handles redirects */}
      <Route path="/" element={<PWASmartStart />} />
      <Route path="/login" element={<PublicRoute><LoginPage /></PublicRoute>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/services/:serviceId" element={<ServicePage />} />
      <Route path="/industries/:industryId" element={<IndustryPage />} />
      <Route path="/team" element={<TeamPage />} />
      <Route path="/about" element={<AboutUsPage />} />
          <Route path="/careers" element={<CareersPage />} />
      
      {/* PWA Share Target - Create ticket from shared content */}
      <Route path="/share-ticket" element={<ShareTicketPage />} />
      
      {/* Admin Routes */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/admin/forgot-password" element={<AdminForgotPasswordPage />} />
      <Route path="/admin/reset-password" element={<AdminResetPasswordPage />} />
      <Route path="/admin" element={<AdminLayout />}>
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="team" element={<AdminTeam />} />
        <Route path="testimonials" element={<AdminTestimonials />} />
        <Route path="technologies" element={<AdminTechnologies />} />
        <Route path="companies" element={<AdminCompanies />} />
        <Route path="customers" element={<AdminCustomers />} />
        <Route path="admins" element={<AdminAdmins />} />
        <Route path="invoices" element={<AdminInvoices />} />
        <Route path="proposals" element={<AdminProposals />} />
        <Route path="dolibarr-quotes" element={<AdminProposals />} />
        <Route path="messages" element={<AdminMessages />} />
        <Route path="tickets" element={<AdminTickets />} />
        <Route path="interventions" element={<AdminInterventions />} />
        <Route path="changes" element={<AdminChanges />} />
        <Route path="login-audit" element={<AdminLoginAudit />} />
        <Route path="social-links" element={<AdminSocialLinks />} />
        <Route path="client-messages" element={<AdminClientMessages />} />
        <Route path="seo" element={<AdminSEO />} />
        <Route path="email-templates" element={<AdminEmailTemplates />} />
        <Route path="activity-log" element={<AdminActivityLog />} />
        <Route path="canned-responses" element={<AdminCannedResponses />} />
        <Route path="api-docs" element={<ApiDocs />} />
        <Route path="performance" element={<PerformanceDashboard />} />
        <Route path="time-tracking" element={<AdminTimeTracking />} />
        <Route path="onboarding" element={<AdminOnboarding />} />
        <Route path="api-settings" element={<AdminApiSettings />} />
        <Route path="remote-sessions" element={<AdminRemoteSessions />} />
        <Route path="integration-sync" element={<AdminIntegrationSync />} />
        <Route path="client-files" element={<AdminClientFiles />} />
        <Route path="faq" element={<AdminFAQ />} />
        <Route path="notification-prefs" element={<AdminNotificationPrefs />} />
        <Route path="network" element={<AdminNetwork />} />
        <Route path="documentation" element={<AdminDocumentation />} />
        <Route path="knowledge-base" element={<AdminKnowledgeBase />} />
        <Route path="ipam" element={<AdminIPAM />} />
        <Route path="assets" element={<AdminAssets />} />
        <Route path="sync-log" element={<AdminSyncLog />} />
        <Route path="push-notifications" element={<AdminPushNotifications />} />
        <Route path="notification-settings" element={<AdminNotificationSettings />} />
        <Route path="email-providers" element={<AdminEmailProviders />} />
        <Route path="notification-log" element={<AdminNotificationLog />} />
        <Route path="contract-dashboard" element={<AdminContractDashboard />} />
        <Route path="contracts" element={<AdminContracts />} />
        <Route path="contracts/new" element={<AdminContractWizard />} />
        <Route path="contracts/:contractId/edit" element={<AdminContractWizard />} />
        <Route path="clauses" element={<AdminClauses />} />
        <Route path="quotes" element={<AdminQuotes />} />
        <Route path="vendor-quotes" element={<AdminVendorQuotes />} />
        <Route path="power-graph" element={<AdminPowerGraph />} />
        <Route path="quotes/new" element={<AdminQuoteBuilder />} />
        <Route path="quotes/:quoteId/edit" element={<AdminQuoteBuilder />} />
        <Route path="quote-workspaces" element={<AdminQuoteWorkspaces />} />
        <Route path="quote-workspace/:workspaceId" element={<AdminQuoteWorkspace />} />
        <Route path="packages" element={<AdminPackages />} />
        <Route path="quote-templates" element={<AdminQuoteTemplates />} />
        <Route path="quote-settings" element={<AdminQuoteSettings />} />
        <Route path="dolibarr-sync" element={<AdminDolibarrSync />} />
        <Route path="glpi-sync" element={<AdminGLPISync />} />
        <Route path="expenses" element={<AdminExpenses />} />
        <Route path="expense-categories" element={<AdminExpenseCategories />} />
        <Route path="admin-roles" element={<AdminRoles />} />
              <Route path="client-roles" element={<AdminClientRoles />} />
              <Route path="careers" element={<AdminCareers />} />
        <Route path="sla-tracking" element={<AdminSLATracking />} />
        <Route path="scheduled-tickets" element={<AdminScheduledTickets />} />
        <Route path="service-catalog" element={<AdminServiceCatalog />} />
        <Route path="client-hours" element={<AdminClientHours />} />
        <Route path="company-identity" element={<AdminCompanyIdentity />} />
        <Route path="security" element={<AdminSecurityDashboard />} />
        <Route path="racks" element={<AdminRackManagement />} />
                        <Route path="racks/utilization" element={<AdminRackUtilization />} />
                        <Route path="maintenance-notices" element={<AdminMaintenanceNotices />} />
        <Route path="assessments" element={<AdminAssessments />} />
        <Route path="assessments/templates" element={<AdminAssessmentTemplates />} />
        <Route path="assessments/:id" element={<AdminAssessmentBuilder />} />
        <Route path="postmortems" element={<AdminPostmortems />} />
        <Route path="postmortems/:id" element={<AdminPostmortemBuilder />} />
                        <Route path="racks/:rackId" element={<AdminRackDesigner />} />
                        <Route path="locations/:locationId/topology" element={<AdminLocationTopology />} />
      </Route>

      {/* Standalone admin pages — no sidebar/chrome (printable reports) */}
      <Route path="/admin/racks/:rackId/report" element={<AdminRackReport />} />
      <Route path="/admin/maintenance-notices/:noticeId/report" element={<AdminMaintenanceReport />} />
      <Route path="/admin/locations/:locationId/report" element={<AdminLocationReport />} />
      <Route path="/dashboard/maintenance-notices/:noticeId/report" element={<ProtectedRoute><AdminMaintenanceReport /></ProtectedRoute>} />
      
      {/* Protected Customer Portal Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
        <Route index element={<Dashboard />} />
        <Route path="tickets" element={<Tickets />} />
        <Route path="tickets/new" element={<CreateTicket />} />
        <Route path="tickets/:id" element={<TicketDetail />} />
        <Route path="invoices" element={<Invoices />} />
        <Route path="proposals" element={<Proposals />} />
        <Route path="dolibarr-quotes" element={<Proposals />} />
        <Route path="messages" element={<Messages />} />
        <Route path="changes" element={<Changes />} />
        <Route path="interventions" element={<Interventions />} />
        <Route path="onboarding" element={<ClientOnboarding />} />
        <Route path="files" element={<ClientFiles />} />
        <Route path="network" element={<ClientNetwork />} />
        <Route path="racks" element={<ClientRackListLazy />} />
        <Route path="racks/:rackId" element={<ClientRackView />} />
        <Route path="maintenance-notices" element={<ClientMaintenanceNotices />} />
        <Route path="knowledge-base" element={<ClientKnowledgeBase />} />
        <Route path="ipam" element={<ClientIPAM />} />
        <Route path="assets" element={<ClientAssets />} />
        <Route path="faq" element={<FAQ />} />
        <Route path="team" element={<Team />} />
        <Route path="account" element={<Account />} />
        <Route path="notifications" element={<ClientNotificationSettings />} />
        <Route path="emails" element={<ClientEmails />} />
        <Route path="contracts" element={<ClientContracts />} />
        <Route path="quotes" element={<ClientQuotes />} />
        <Route path="service-catalog" element={<ClientServiceCatalog />} />
        <Route path="hours" element={<ClientHours />} />
        <Route path="assessments" element={<ClientAssessments />} />
        <Route path="expenses" element={<ClientExpenses />} />
      </Route>
      
      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </Suspense>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <AppRoutes />
          <Toaster position="top-right" richColors />
          <PWAInstallPrompt />
          <NetworkStatus />
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
